'use client';
import { useCallback } from 'react';

import { useMixpanel } from '@/hooks/useMixpanel';
import { useOpenAuthModal } from '@/hooks/useOpenAuthModal';
import { useToggleBookmark } from '@/services/query/bookmark.queries';
import { store, useAppSelector, useAuth } from '@/store';
import type { EventResponse, Market } from '@/types';

/**
 * Reactive per-id bookmark check.
 *
 * Unlike subscribing to the whole `bookmarkedEventIds` array, this selects only
 * the boolean for `id`, so a component re-renders ONLY when its own bookmark
 * status flips — not every time any bookmark anywhere is toggled. Use this in
 * list cards (rendered N times) to avoid an all-cards re-render cascade.
 */
export const useIsBookmarked = (id?: string): boolean =>
  useAppSelector((s) => (id ? s.bookmarks.bookmarkedEventIds.includes(id) : false));

/**
 * Bookmark toggle action WITHOUT any reactive subscription to the bookmark list.
 *
 * The current bookmark state (needed only for the one-time "bookmarked" analytics
 * event) is read non-reactively from the store, so a component that only needs to
 * toggle bookmarks doesn't re-render whenever the list changes.
 */
export const useBookmarkToggle = () => {
  const { trackMarketBookmarked } = useMixpanel();
  const { isAuthenticated } = useAuth();
  const { mutate: toggleBookmark } = useToggleBookmark();
  const openAuthModal = useOpenAuthModal();

  return useCallback(
    (
      e: React.MouseEvent | undefined,
      item: Market | EventResponse | null,
      _type: 'market' | 'sport',
      _customUrl?: string,
    ) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!isAuthenticated) {
        openAuthModal();
        return;
      }

      if (!item) return;

      // Fallback to id if eventId is not present. Both Market and EventResponse have id.
      const eventId = (item as any).eventId || item.id;

      if (eventId) {
        // Non-reactive read: we only need the current value to decide whether to
        // fire the "bookmarked" analytics event, not to trigger re-renders.
        const isCurrentlyBookmarked =
          store.getState().bookmarks.bookmarkedEventIds.includes(eventId);
        if (!isCurrentlyBookmarked) {
          trackMarketBookmarked({
            market_id: eventId,
            market_title: (item as any).title || (item as any).question || item.description || '',
            category: (item as any).category || (item as any).groupItemTitle || '',
          });
        }
        toggleBookmark(eventId);
      }
    },
    [isAuthenticated, toggleBookmark, openAuthModal, trackMarketBookmarked],
  );
};

/**
 * Backward-compatible combined hook.
 *
 * Kept for consumers that render a single card (detail pages), where subscribing
 * to the whole bookmark array is cheap. High-frequency list cards should instead
 * use `useIsBookmarked(id)` + `useBookmarkToggle()` to avoid re-render cascades.
 */
export const useEventBookmarkAction = () => {
  const bookmarkedEventIds = useAppSelector((s) => s.bookmarks.bookmarkedEventIds);
  const handleBookmarkToggle = useBookmarkToggle();

  const checkIsBookmarked = useCallback(
    (id: string) => {
      return bookmarkedEventIds.includes(id);
    },
    [bookmarkedEventIds],
  );

  return { checkIsBookmarked, handleBookmarkToggle };
};
